import { Alert, Box, Paper, Typography } from "@mui/material";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { ComparationChange } from "../../types/ComparationChange";
import { ComparationEventField } from "../../types/ComparationEventField";
import "./ComparationEventSelector.scss";

interface Props {
	info: string;
	emptyMessage: string;
	events: ComparationChange[];
	selected: number | undefined;
	onSelect: (eventId: number) => void;
	children?: ReactNode;
}

const contractChangeLabels = [
	'numero de inscricao',
	'data da alteracao',
	'nome do cargo',
	'classificacao brasileira de ocupacao'
];
const absenceHiddenLabels = ['empregador'];
const dismissalHiddenLabels = ['empregador', 'pagamento do aviso', 'grau de exposicao'];

const normalizeLabel = (value: string) => (value || '')
	.normalize('NFD')
	.replace(/[\u0300-\u036f]/g, '')
	.trim()
	.toLowerCase();

const ComparationEventSelector: FC<Props> = (props: Props) => {
	const [hasMore, setHasMore] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	const verifyScroll = () => {
		const element = scrollRef.current;
		if (!element) {
			return;
		}
		setHasMore(element.scrollWidth - element.clientWidth - element.scrollLeft > 8);
	};

	useEffect(() => {
		verifyScroll();
	}, [props.events]);

	const visibleFields = (event: ComparationChange) => {
		const fields = event.fields || [];
		if (event.eventType === 'CONTRACT_CHANGE') {
			return contractChangeLabels
				.map(term => fields.find(field => normalizeLabel(field.label).includes(normalizeLabel(term))))
				.filter(field => !!field) as ComparationEventField[];
		}
		const hiddenLabels = event.eventType === 'DISMISSAL' ? dismissalHiddenLabels : absenceHiddenLabels;
		return fields.filter(field => !hiddenLabels
			.some(term => normalizeLabel(field.label).includes(normalizeLabel(term))));
	};

	return (
		<Box className="event-selector">
			{!props.events.length
				? <Alert severity="warning" variant="outlined">{props.emptyMessage}</Alert>
				: <Box className="event-selector-content">
					<Alert severity="info" variant="outlined">{props.info}</Alert>
					<Box className="event-options-header">
						<Typography variant="caption">
							{props.events.length === 1
								? '1 evento encontrado'
								: `${props.events.length} eventos encontrados, do mais recente para o mais antigo`}
						</Typography>
						{hasMore && <Typography variant="caption">Role para o lado para ver mais</Typography>}
					</Box>
					<Box className={hasMore ? "event-options-scroll has-more" : "event-options-scroll"}>
						<Box className="event-options" ref={scrollRef} onScroll={verifyScroll}>
							{props.events.map(event => (
								<Paper
									key={event.eventId}
									className={props.selected === event.eventId ? "event-option selected" : "event-option"}
									onClick={() => props.onSelect(event.eventId)}
								>
									<Typography className="event-title">{event.eventDescription || 'Evento do eSocial'}</Typography>
									{visibleFields(event).map((field, index) => (
										<Typography key={index} className="event-field" variant="body2">
											<b>{field.label}:</b> {field.value}
										</Typography>
									))}
								</Paper>
							))}
						</Box>
					</Box>
				</Box>
			}
			{props.children}
		</Box>
	);
}

export default ComparationEventSelector;